import { useEffect, useState } from 'react';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import colors from 'styles/theme/colors';
import CircleCheckIcon from 'svg/CircleCheckIcon';
import CircleXIcon from 'svg/CircleXIcon';
import XIcon2 from 'svg/XIcon2';

const TRANSITION_TIME = 300;

export enum NotificationState {
  PROCESSING = 'processing',
  FAILURE = 'failed',
  SUCCESS = 'completed',
}

export interface NotificationProps {
  state: NotificationState;
  title: string;
  timestamp?: string;
  onActionClick?: () => void;
  onDismiss?: () => void;
  actionSuccessText?: string;
  actionFailureText?: string;
}

function Notification({
  state,
  title,
  timestamp,
  onActionClick,
  onDismiss,
  actionSuccessText = 'View Summary',
  actionFailureText = 'Try Again',
}: NotificationProps) {
  const [hasTransitioningStarted, setHasTransitionStarted] = useState(false);
  const [hasTransitionEnded, setHasTransitionEnded] = useState(false);

  const [previousState, setPreviousState] = useState<NotificationState>(state);

  useEffect(() => {
    if (state !== previousState && previousState === NotificationState.PROCESSING) {
      setHasTransitionStarted(true);
      const timer = setTimeout(() => {
        setHasTransitionEnded(true);
      }, TRANSITION_TIME);
      return () => clearTimeout(timer);
    } else {
      setPreviousState(state);
    }
  }, [state, previousState]);

  let toColor: string | undefined;
  switch (state) {
    case NotificationState.SUCCESS:
      toColor = colors.green100;
      break;
    case NotificationState.FAILURE:
      toColor = colors.red100;
      break;
  }

  return (
    <NotificationContainer>
      <IconContainer>
        {!hasTransitionEnded && (
          <IconWrapper $fadeOut={hasTransitioningStarted} $toColor={toColor}>
            {previousState === NotificationState.PROCESSING && (
              <CircularProgress size={22} thickness={2} color={colors.blue100} />
            )}
            {previousState === NotificationState.SUCCESS && (
              <CircleCheckIcon fill={colors.green100} width='22' height='22' />
            )}
            {previousState === NotificationState.FAILURE && <CircleXIcon fill={colors.red100} width='22' height='22' />}
          </IconWrapper>
        )}
        {(hasTransitioningStarted || hasTransitionEnded) && (
          <IconWrapper $fadeIn>
            {state === NotificationState.SUCCESS && <CircleCheckIcon fill={colors.green100} width='22' height='22' />}
            {state === NotificationState.FAILURE && <CircleXIcon fill={colors.red100} width='22' height='22' />}
          </IconWrapper>
        )}
      </IconContainer>

      <ContentContainer>
        <DescriptionText>{title}</DescriptionText>
        {timestamp && <TimestampText>{timestamp}</TimestampText>}
        {onActionClick && (
          <ActionLink onClick={onActionClick} data-cy='inAppNotificationLink'>
            {state === NotificationState.SUCCESS ? actionSuccessText : actionFailureText}
          </ActionLink>
        )}
      </ContentContainer>

      {onDismiss && (
        <DismissButton onClick={onDismiss}>
          <XIcon2 width={8} height={8} />
        </DismissButton>
      )}
    </NotificationContainer>
  );
}

const NotificationContainer = styled.div`
  position: relative;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  box-shadow: 0 4px 4px 0 ${colors.black5};
  padding: 24px 32px 24px 24px;
  display: flex;
  gap: 24px;
  width: 400px;

  @media (max-width: 431px) {
    width: calc(100vw - 32px);
  }
`;

const IconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const IconWrapper = styled.div<{ $fadeOut?: boolean; $fadeIn?: boolean; $toColor?: string }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.$fadeOut ? 0 : 1)};
  animation: ${(props) => (props.$fadeIn ? `fadeIn ${TRANSITION_TIME}ms ease-in` : 'none')};
  transition: opacity ${TRANSITION_TIME}ms ease-out;

  .spinner {
    border-left-color: ${(props) => (props.$fadeOut && props.$toColor ? props.$toColor : colors.blue100)};
    transition: border-left-color ${TRANSITION_TIME / 2}ms ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DescriptionText = styled.p`
  font-size: 14px;
  font-weight: normal;
  color: ${colors.black100};
  margin: 0;
`;

const TimestampText = styled.p`
  font-size: 14px;
  font-weight: normal;
  color: ${colors.black100};
  margin: 0;
`;

const ActionLink = styled.button`
  font-size: 14px;
  color: ${colors.blue100};
  font-weight: normal;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: none;
  }
`;

const DismissButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default Notification;
